import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import ComercioNombre from '../components/ComercioNombre';
import ActivarAvisos from '../components/ActivarAvisos';
import { quitarAvisos } from '../lib/avisosWeb';
import { fmtARS } from '../lib/money';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/Toast';
import AYUDA from '../lib/ayuda.json';
import { DELIVERY, DELIVERY_SRCSET, DESCUENTOS, DESCUENTOS_SRCSET } from '../lib/imagenes';
import '../styles/Regalos.css';
import '../styles/AdminPage.css';

// Panel de los comercios de Cupido Delivery: registro, ingreso, productos y pedidos.
// La sesión del comercio es aparte de la de los usuarios de la app.

const CATEGORIAS = ['flores', 'bombones', 'desayunos', 'bebidas', 'artesanales', 'otros'];
const LEYENDA = 'Cupido con Mate no se hace responsable de los envíos. El envío lo gestiona el comercio por su cuenta (PedidosYa, Uber u otro medio) y es exclusiva responsabilidad del comercio que el producto llegue al destinatario.';
const LEYENDA_FACTURACION = 'El comercio es el encargado de facturar el 100% de la venta y de emitir la factura correspondiente.';
const STATUS = {
  paid: ['Esperando que lo acepten', 'badge-dorado'],
  accepted: ['Para entregar', 'badge-verde'],
  delivered: ['Entregado', 'badge-gris'],
  rejected: ['Rechazado (devuelto)', 'badge-gris'],
  refunded: ['Devuelto', 'badge-gris'],
  expired: ['Vencido (devuelto)', 'badge-gris'],
};
const AYUDA_COMERCIOS = AYUDA.secciones.find(s => s.id === 'comercios');
const AYUDA_DESCUENTOS = AYUDA.secciones.find(s => s.id === 'locales-descuentos');
const fecha = (s) => s ? new Date(String(s).replace(' ', 'T') + 'Z').toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '';

function Leyenda() {
  return <div className="comercio-leyenda">⚠️ {LEYENDA}<br />🧾 {LEYENDA_FACTURACION}</div>;
}

function AyudaModal({ onClose, marca }) {
  const ayuda = marca === 'descuentos' ? AYUDA_DESCUENTOS : AYUDA_COMERCIOS;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal regalar-modal" onClick={e => e.stopPropagation()}>
        <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: 12 }}>{ayuda.icono} {ayuda.titulo}</h2>
        <ol className="comercio-ayuda-pasos">
          {ayuda.pasos.map((p, i) => <li key={i}>{p}</li>)}
        </ol>
        <p className="regalo-note" style={{ marginBottom: 16 }}>💡 {ayuda.tip}</p>
        {marca !== 'descuentos' && <Leyenda />}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
          <a className="btn btn-outline" href={`/ayuda/${ayuda.id}`}>Abrir en el centro de ayuda</a>
          <button className="btn btn-primary" onClick={onClose}>Entendido</button>
        </div>
      </div>
    </div>
  );
}

// Fecha máxima para tener 18 años hoy (AAAA-MM-DD)
const fechaMayor = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().slice(0, 10); };

// Botón de Google: usa la misma cuenta de Gmail para registrarse e ingresar
function BotonGoogle({ marca, texto }) {
  return (
    <a className="btn btn-outline comercio-google" href={`/api/comercio/google?panel=${marca}`}>
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      {texto}
    </a>
  );
}

// Completar la fecha de nacimiento (comercios que se registraron antes de pedirla)
function Nacimiento({ onCambio, error }) {
  const [f, setF] = useState('');
  const [busy, setBusy] = useState(false);
  const guardar = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { onCambio((await api.comercioNacimiento(f)).comercio); } catch (err) { error(err.message); } finally { setBusy(false); }
  };
  return (
    <form className="comercio-card" onSubmit={guardar} style={{ maxWidth: 480, margin: '0 auto 16px' }}>
      <h3 style={{ fontFamily: 'var(--font-display)', color: 'white', marginBottom: 6 }}>🎂 Tu fecha de nacimiento</h3>
      <p className="regalo-note" style={{ marginBottom: 12 }}>El panel es solo para mayores de 18 años. Completala para seguir usando tu panel.</p>
      <input className="form-input" type="date" required max={fechaMayor()} value={f} onChange={e => setF(e.target.value)} aria-label="Fecha de nacimiento" />
      <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 12 }} disabled={busy}>{busy ? 'Guardando...' : 'Guardar'}</button>
    </form>
  );
}

// Desglose que el comercio ve antes de guardar: su precio + envío + 10% de la app = precio final
function Desglose({ precio, envio, pct }) {
  const p = parseInt(precio, 10) || 0;
  const e = parseInt(envio, 10) || 0;
  const comision = Math.round((p + e) * pct / 100);
  return (
    <div className="comercio-desglose">
      <div><span>Tu precio</span><span>{fmtARS(p)}</span></div>
      <div><span>Tu costo de envío</span><span>{fmtARS(e)}</span></div>
      <div className="comercio-desglose-tuyo"><span>Recibís vos</span><span>{fmtARS(p + e)}</span></div>
      <div><span>+ {pct}% Cupido con Mate (comercios destacados de Cupido Delivery)</span><span>{fmtARS(comision)}</span></div>
      <div className="comercio-desglose-total"><span>Precio final que ve el cliente</span><span>{fmtARS(p + e + comision)}</span></div>
    </div>
  );
}

// ── Ingreso, registro, recuperar y elegir contraseña ──────────
// marca: 'delivery' (/comercio, vende regalos) o 'descuentos' (/descuentos, bares, restaurantes, alojamientos...)
function Acceso({ marca, onListo, success, error }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  const desc = marca === 'descuentos';
  const [modo, setModo] = useState(token ? 'activar' : params.get('registro') ? 'registro' : 'ingresar');
  const [ayuda, setAyuda] = useState(false);
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState(null); // { email, nombre } si viene de "Continuar con Google" sin comercio
  const [f, setF] = useState({ nombre: '', categoria: 'flores', descripcion: '', email: '', telefono: '', password: '', password2: '', nacimiento: '', acepto: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  useEffect(() => {
    const err = params.get('error');
    if (err) error({ google: 'No pudimos ingresar con Google. Probá de nuevo.', rechazado: 'Tu solicitud no fue aprobada.', pausado: 'Tu panel está pausado. Escribile al equipo de Cupido con Mate.' }[err] || 'No pudimos ingresar.');
    if (params.get('registro') === 'google') {
      api.comercioGooglePendiente().then(g => { setGoogle(g); setF(x => ({ ...x, email: g.email })); })
        .catch(() => error('La sesión de Google venció. Tocá "Continuar con Google" de nuevo.'));
    }
  }, []);

  const enviar = async (e) => {
    e.preventDefault();
    const conGoogle = modo === 'registro' && google;
    if ((modo === 'activar' || (modo === 'registro' && !conGoogle)) && f.password !== f.password2) { error('Las contraseñas no coinciden.'); return; }
    if (modo === 'registro' && !desc && !f.acepto) { error('Tenés que aceptar las condiciones de envío.'); return; }
    setBusy(true);
    try {
      if (modo === 'ingresar') onListo((await api.comercioIngresar(f.email.trim(), f.password)).comercio);
      if (modo === 'activar') { onListo((await api.comercioActivar(token, f.password)).comercio); navigate(location.pathname.replace('/activar', ''), { replace: true }); }
      if (modo === 'olvide') { success((await api.comercioOlvide(f.email.trim())).message); setModo('ingresar'); }
      if (modo === 'registro') {
        const { comercio } = await api.comercioRegistro({
          nombre: f.nombre.trim(), categoria: f.categoria, descripcion: f.descripcion.trim(), email: f.email.trim(),
          telefono: f.telefono.trim(), fecha_nacimiento: f.nacimiento, acepto: desc ? 'false' : 'true',
          en_delivery: !desc, en_descuentos: desc, ...(conGoogle ? {} : { password: f.password }),
        });
        success('¡Listo! Tu solicitud quedó en revisión.');
        navigate(location.pathname, { replace: true });
        onListo(comercio);
      }
    } catch (err) { error(err.message); }
    finally { setBusy(false); }
  };

  const titulos = { ingresar: 'Ingresá a tu panel', registro: desc ? 'Registrá tu local' : 'Registrá tu comercio', olvide: 'Recuperar contraseña', activar: 'Elegí tu contraseña' };
  const pideClave = modo === 'ingresar' || modo === 'activar' || (modo === 'registro' && !google);

  return (
    <div className="comercio-acceso">
      <form className="comercio-card" onSubmit={enviar}>
        <div className="regalo-head" style={{ marginBottom: 14 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'white' }}>{titulos[modo]}</h2>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setAyuda(true)}>❓ Ayuda</button>
        </div>
        {modo === 'registro' && (
          <p className="regalo-note" style={{ marginBottom: 14 }}>
            {desc
              ? 'Sumá tu bar, restaurante, café, alojamiento u otro lugar para citas a Cupido Descuentos y publicá descuentos para las parejas de San Juan. Revisamos cada solicitud antes de publicarla.'
              : 'Sumá tu comercio a Cupido Delivery y vendé regalos a las parejas de San Juan. Revisamos cada solicitud antes de publicarla.'}
          </p>
        )}
        {(modo === 'ingresar' || (modo === 'registro' && !google)) && (
          <>
            <BotonGoogle marca={marca} texto={modo === 'registro' ? 'Registrarme con Google' : 'Ingresar con Google'} />
            <div className="comercio-o"><span>o con tu correo</span></div>
          </>
        )}
        {modo === 'registro' && google && (
          <div className="comercio-aviso" style={{ marginBottom: 14 }}>✅ Te registrás con tu cuenta de Google <strong>{google.email}</strong>. No hace falta contraseña.</div>
        )}
        {modo === 'registro' && (
          <div className="comercio-form-grid">
            <div className="form-group"><label className="form-label">{desc ? 'Nombre del local' : 'Nombre del comercio'}</label>
              <input className="form-input" value={f.nombre} maxLength={80} onChange={set('nombre')} required /></div>
            {!desc && (
              <div className="form-group"><label className="form-label">Categoría</label>
                <select className="form-input" value={f.categoria} onChange={set('categoria')}>
                  {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
            )}
            <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">Descripción corta</label>
              <input className="form-input" value={f.descripcion} maxLength={300} onChange={set('descripcion')} placeholder={desc ? 'Bar con terraza, cocina regional, habitaciones temáticas...' : 'Ramos, cajas de bombones, desayunos sorpresa...'} /></div>
            <div className="form-group"><label className="form-label">Teléfono de contacto</label>
              <input className="form-input" value={f.telefono} maxLength={40} onChange={set('telefono')} required /></div>
            <div className="form-group"><label className="form-label">Tu fecha de nacimiento</label>
              <input className="form-input" type="date" value={f.nacimiento} max={fechaMayor()} onChange={set('nacimiento')} required /></div>
          </div>
        )}
        {modo === 'registro' && <p className="regalo-note" style={{ margin: '-4px 0 12px' }}>🔞 El panel es solo para mayores de 18 años.</p>}
        {modo !== 'activar' && !(modo === 'registro' && google) && (
          <div className="form-group" style={{ marginBottom: 12 }}><label className="form-label">Correo</label>
            <input className="form-input" type="email" value={f.email} onChange={set('email')} required autoComplete="email" /></div>
        )}
        {pideClave && (
          <div className="comercio-form-grid">
            <div className="form-group"><label className="form-label">Contraseña</label>
              <input className="form-input" type="password" value={f.password} onChange={set('password')} required
                minLength={modo === 'ingresar' ? 1 : 8} autoComplete={modo === 'ingresar' ? 'current-password' : 'new-password'} /></div>
            {modo !== 'ingresar' && (
              <div className="form-group"><label className="form-label">Repetí la contraseña</label>
                <input className="form-input" type="password" value={f.password2} onChange={set('password2')} required minLength={8} autoComplete="new-password" /></div>
            )}
          </div>
        )}
        {modo === 'registro' && !desc && (
          <>
            <Leyenda />
            <label className="comercio-check">
              <input type="checkbox" checked={f.acepto} onChange={set('acepto')} />
              <span>Acepto que el envío es mi responsabilidad, que facturo el 100% de cada venta y emito la factura, y que al precio de mis productos se le suma un 10% para Cupido con Mate por figurar en los comercios destacados de Cupido Delivery.</span>
            </label>
          </>
        )}
        <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={busy}>
          {busy ? 'Un momento...' : { ingresar: 'Ingresar', registro: 'Enviar solicitud', olvide: 'Mandarme el enlace', activar: 'Guardar y entrar' }[modo]}
        </button>
        <div className="comercio-links">
          {modo === 'ingresar' && <>
            <button type="button" onClick={() => setModo('registro')}>{desc ? '¿Todavía no estás? Registrá tu local' : '¿Todavía no estás? Registrá tu comercio'}</button>
            <button type="button" onClick={() => setModo('olvide')}>Olvidé mi contraseña</button>
          </>}
          {modo !== 'ingresar' && <button type="button" onClick={() => { setModo('ingresar'); setGoogle(null); }}>Ya tengo cuenta: ingresar</button>}
        </div>
      </form>
      {ayuda && <AyudaModal marca={marca} onClose={() => setAyuda(false)} />}
    </div>
  );
}

// ── Productos ─────────────────────────────────────────────────
function ProductoForm({ inicial, pct, onGuardar, onCancelar }) {
  const [f, setF] = useState({
    nombre: inicial?.nombre || '', descripcion: inicial?.descripcion || '',
    precio: inicial?.precio ?? '', costo_envio: inicial?.costo_envio ?? '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onGuardar({
      nombre: f.nombre.trim(), descripcion: f.descripcion.trim(),
      precio: parseInt(f.precio, 10) || 0, costo_envio: parseInt(f.costo_envio, 10) || 0,
    });
    setBusy(false);
  };
  return (
    <form className="comercio-card" onSubmit={submit}>
      <h3 style={{ fontFamily: 'var(--font-display)', color: 'white', marginBottom: 14 }}>{inicial ? `Editar: ${inicial.nombre}` : 'Nuevo producto'}</h3>
      <div className="comercio-form-grid">
        <div className="form-group"><label className="form-label">Nombre</label>
          <input className="form-input" value={f.nombre} maxLength={80} onChange={set('nombre')} required /></div>
        <div className="form-group"><label className="form-label">Descripción</label>
          <input className="form-input" value={f.descripcion} maxLength={300} onChange={set('descripcion')} /></div>
        <div className="form-group"><label className="form-label">Tu precio (ARS)</label>
          <input className="form-input" type="number" min="100" step="1" value={f.precio} onChange={set('precio')} required /></div>
        <div className="form-group"><label className="form-label">Tu costo de envío (ARS)</label>
          <input className="form-input" type="number" min="0" step="1" value={f.costo_envio} onChange={set('costo_envio')} required placeholder="Lo que te cobra PedidosYa o Uber" /></div>
      </div>
      <Desglose precio={f.precio} envio={f.costo_envio} pct={pct} />
      <p className="regalo-note" style={{ margin: '10px 0' }}>
        El envío lo hacés vos por PedidosYa, Uber o tu cadete. Mercado Pago puede descontar su propio costo por cobrar, según tu cuenta.
      </p>
      <div className="regalo-actions">
        {onCancelar && <button type="button" className="btn btn-outline btn-sm" onClick={onCancelar}>Cancelar</button>}
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Guardando...' : inicial ? 'Guardar cambios' : '+ Agregar producto'}</button>
      </div>
    </form>
  );
}

function Productos({ comercio, success, error }) {
  const [productos, setProductos] = useState(null);
  const [editando, setEditando] = useState(null);
  const [nuevoKey, setNuevoKey] = useState(0);
  const load = useCallback(() => api.comercioProductos().then(d => setProductos(d.productos || [])).catch(e => error(e.message)), [error]);
  useEffect(() => { load(); }, [load]);

  const guardar = (id) => async (data) => {
    try {
      if (id) await api.comercioEditarProducto(id, data); else await api.comercioCrearProducto(data);
      success(id ? 'Producto actualizado.' : 'Producto agregado.');
      setEditando(null);
      if (!id) setNuevoKey(k => k + 1); // vacía el formulario de nuevo producto
      load();
      return true;
    } catch (err) { error(err.message); return false; }
  };
  const toggle = async (p) => {
    try { await api.comercioEditarProducto(p.id, { activo: !p.activo }); load(); } catch (err) { error(err.message); }
  };
  const foto = async (p, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('photo', file);
    try { await api.comercioFotoProducto(p.id, fd); success('Foto actualizada.'); load(); } catch (err) { error(err.message); }
  };

  return (
    <div>
      {editando === null && <ProductoForm key={`nuevo-${nuevoKey}`} pct={comercio.comision_pct} onGuardar={guardar(null)} />}
      {productos === null ? <p className="regalos-empty">Cargando...</p>
        : productos.length === 0 ? <p className="regalos-empty">Todavía no cargaste productos.</p>
        : productos.map(p => editando === p.id ? (
          <ProductoForm key={p.id} inicial={p} pct={comercio.comision_pct} onGuardar={guardar(p.id)} onCancelar={() => setEditando(null)} />
        ) : (
          <div key={p.id} className="comercio-producto" style={{ marginBottom: 8, ...(p.activo ? {} : { opacity: 0.6 }) }}>
            {p.foto_path ? <img src={p.foto_path} alt={p.nombre} /> : <div className="comercio-sin-foto">📷</div>}
            <div style={{ flex: 1, minWidth: 160 }}>
              <div className="regalo-name" style={{ fontSize: 14 }}>{p.nombre} {!p.activo && <span className="badge badge-gris">Pausado</span>}</div>
              <div className="regalo-detail" style={{ marginTop: 0 }}>
                Recibís {fmtARS(p.subtotal)} ({fmtARS(p.precio)} + envío {fmtARS(p.costo_envio)}) · El cliente paga <strong>{fmtARS(p.total)}</strong>
              </div>
            </div>
            <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
              📷 Foto
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { foto(p, e.target.files[0]); e.target.value = ''; }} />
            </label>
            <button className="btn btn-outline btn-sm" onClick={() => setEditando(p.id)}>✏️ Editar</button>
            <button className="btn btn-outline btn-sm" onClick={() => toggle(p)}>{p.activo ? 'Pausar' : 'Activar'}</button>
          </div>
        ))}
    </div>
  );
}

// ── Pedidos ───────────────────────────────────────────────────
function Pedido({ p, medios, onCambio, error }) {
  const [medio, setMedio] = useState(p.envio_medio || medios[0]);
  const [busy, setBusy] = useState(false);
  const accion = async (fn, confirmar) => {
    if (confirmar && !window.confirm(confirmar)) return;
    setBusy(true);
    try { await fn(); onCambio(); } catch (err) { error(err.message); } finally { setBusy(false); }
  };
  const [label, badge] = STATUS[p.status] || [p.status, 'badge-gris'];
  return (
    <div className="comercio-card">
      <div className="regalo-head">
        <span className="regalo-name">🎁 {p.producto_nombre}</span>
        <span className={`badge ${badge}`}>{p.status === 'accepted' && p.enviado_at ? `En camino (${p.envio_medio})` : label}</span>
      </div>
      <div className="regalo-detail">
        {fecha(p.paid_at || p.created_at)} · Recibís {fmtARS(p.cobras)} ({fmtARS(p.precio_producto)} + envío {fmtARS(p.costo_envio)}) · El cliente pagó {fmtARS(p.total)}
      </div>
      {p.status === 'paid' && <p className="regalo-note" style={{ marginTop: 8 }}>Ya está pago. Esperamos que la persona lo acepte y cargue la dirección (tiene 7 días). Todavía no lo prepares.</p>}
      {p.entrega && (
        <>
          <div className="comercio-entrega">
            <div><strong>Para:</strong> {p.entrega.para}</div>
            <div><strong>Teléfono:</strong> {p.entrega.telefono || 'no informado'}</div>
            <div><strong>Dirección:</strong> {p.entrega.direccion}</div>
            {p.entrega.referencias && <div><strong>Referencias:</strong> {p.entrega.referencias}</div>}
            {p.entrega.horario && <div><strong>Horario:</strong> {p.entrega.horario}</div>}
            {p.entrega.lat != null && (
              <div><strong>Mapa:</strong> <a href={`https://www.google.com/maps?q=${p.entrega.lat},${p.entrega.lng}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dorado-claro)' }}>ver el punto de entrega</a></div>
            )}
            {p.entrega.tarjeta
              ? <div><strong>Tarjeta:</strong> “{p.entrega.tarjeta}” — de {p.entrega.de}</div>
              : <div><strong>De:</strong> {p.entrega.de}</div>}
          </div>
          <div className="regalo-actions">
            {!p.enviado_at && (
              <>
                <select className="form-input" style={{ width: 'auto' }} value={medio} onChange={e => setMedio(e.target.value)}>
                  {medios.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => accion(() => api.comercioEnviado(p.id, medio))}>🛵 Marcar enviado</button>
              </>
            )}
            <button className="btn btn-primary btn-sm" disabled={busy}
              onClick={() => accion(() => api.comercioEntregado(p.id), '¿Confirmás que el regalo ya llegó a destino?')}>✅ Marcar entregado</button>
          </div>
          <p className="regalo-note" style={{ marginTop: 8 }}>Los datos de entrega se ocultan cuando marcás el pedido como entregado.</p>
        </>
      )}
    </div>
  );
}

function Pedidos({ comercio, error }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.comercioPedidos().then(setD).catch(e => error(e.message)), [error]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="regalos-empty">Cargando...</p>;
  const pendientes = d.pedidos.filter(p => p.status === 'accepted' || p.status === 'paid');
  const resto = d.pedidos.filter(p => !pendientes.includes(p));
  return (
    <div>
      <div className="comercio-totales">
        <div><span>Este mes</span><strong>{fmtARS(d.totales.cobrado_mes)}</strong></div>
        <div><span>Total cobrado</span><strong>{fmtARS(d.totales.cobrado)}</strong></div>
        <div><span>Pedidos</span><strong>{d.totales.cantidad}</strong></div>
      </div>
      <h3 className="regalos-section-title">Por entregar</h3>
      {pendientes.length === 0 ? <p className="regalos-empty">No tenés pedidos pendientes.</p>
        : pendientes.map(p => <Pedido key={p.id} p={p} medios={comercio.medios_envio} onCambio={load} error={error} />)}
      {resto.length > 0 && <>
        <h3 className="regalos-section-title">Anteriores</h3>
        {resto.map(p => <Pedido key={p.id} p={p} medios={comercio.medios_envio} onCambio={load} error={error} />)}
      </>}
    </div>
  );
}

// ── Cupido Descuentos: descuentos del local y validar códigos ─
const DESCUENTO_VACIO = { categoria: 'Bar', descuento: '', descripcion: '', direccion: '', condiciones: '' };

function DescuentoForm({ inicial, categorias, onGuardar, onCancelar }) {
  const [f, setF] = useState(inicial ? {
    categoria: inicial.categoria, descuento: inicial.descuento, descripcion: inicial.descripcion || '',
    direccion: inicial.direccion || '', condiciones: inicial.condiciones || '',
  } : DESCUENTO_VACIO);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onGuardar({ ...f, descuento: f.descuento.trim(), descripcion: f.descripcion.trim(), direccion: f.direccion.trim(), condiciones: f.condiciones.trim() });
    setBusy(false);
  };
  return (
    <form className="comercio-card" onSubmit={submit}>
      <h3 style={{ fontFamily: 'var(--font-display)', color: 'white', marginBottom: 14 }}>{inicial ? 'Editar descuento' : 'Nuevo descuento'}</h3>
      <div className="comercio-form-grid">
        <div className="form-group"><label className="form-label">Tipo de lugar</label>
          <select className="form-input" value={f.categoria} onChange={set('categoria')}>
            {categorias.map(c => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Descuento</label>
          <input className="form-input" value={f.descuento} maxLength={60} onChange={set('descuento')} required placeholder="Ej: 20% en la cuenta, 2x1 en tragos" /></div>
        <div className="form-group"><label className="form-label">Dirección</label>
          <input className="form-input" value={f.direccion} maxLength={150} onChange={set('direccion')} placeholder="Ej: Av. Libertador 1234, Capital" /></div>
        <div className="form-group"><label className="form-label">Condiciones</label>
          <input className="form-input" value={f.condiciones} maxLength={300} onChange={set('condiciones')} placeholder="Ej: de lunes a jueves, para 2 personas" /></div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">Descripción</label>
          <input className="form-input" value={f.descripcion} maxLength={300} onChange={set('descripcion')} placeholder="Contá por qué tu lugar es ideal para una cita" /></div>
      </div>
      <div className="regalo-actions">
        {onCancelar && <button type="button" className="btn btn-outline btn-sm" onClick={onCancelar}>Cancelar</button>}
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Guardando...' : inicial ? 'Guardar cambios' : '+ Publicar descuento'}</button>
      </div>
    </form>
  );
}

function ValidarCodigo({ onValidado }) {
  const [codigo, setCodigo] = useState('');
  const [resultado, setResultado] = useState(null);
  const [busy, setBusy] = useState(false);
  const validar = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.comercioValidarCodigo(codigo.trim());
      setResultado({ ok: true, texto: `✅ Código ${r.codigo} válido: ${r.descuento}${r.condiciones ? ` (${r.condiciones})` : ''}. Ya quedó usado.` });
      setCodigo('');
      onValidado();
    } catch (err) { setResultado({ ok: false, texto: `❌ ${err.message}` }); }
    finally { setBusy(false); }
  };
  return (
    <form className="comercio-card" onSubmit={validar}>
      <h3 style={{ fontFamily: 'var(--font-display)', color: 'white', marginBottom: 6 }}>🎟️ Validar código</h3>
      <p className="regalo-note" style={{ marginBottom: 12 }}>Cuando una pareja te muestre su código CUPIDO-XXXXXX, escribilo acá antes de cobrar. Cada código sirve una sola vez.</p>
      <div className="regalo-actions" style={{ marginTop: 0 }}>
        <input className="form-input" style={{ flex: 1, minWidth: 180, textTransform: 'uppercase' }} value={codigo} maxLength={20}
          onChange={e => { setCodigo(e.target.value); setResultado(null); }} placeholder="CUPIDO-A1B2C3" required aria-label="Código" />
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Validando...' : 'Validar'}</button>
      </div>
      {resultado && <div className={resultado.ok ? 'comercio-validado' : 'comercio-invalido'}>{resultado.texto}</div>}
    </form>
  );
}

// Destacado: el local sale primero, como lugar recomendado en el chat y sugerido en Cita segura.
// Es una suscripción mensual de Mercado Pago que se cobra sola; se puede dar de baja cuando quiera.
function Destacado({ comercio, estado, onCambio, success, error }) {
  const [busy, setBusy] = useState(false);
  const accion = async (fn) => { setBusy(true); try { await fn(); } catch (err) { error(err.message); } finally { setBusy(false); } };
  const prueba = () => accion(async () => { await api.comercioDestacadoPrueba(); success('🎉 ¡Listo! Tu local está destacado gratis por 30 días.'); onCambio(); });
  const suscribir = () => accion(async () => {
    window.location.href = (await api.comercioDestacadoSuscribir(window.location.pathname.startsWith('/descuentos') ? 'descuentos' : 'delivery')).url;
  });
  const baja = () => {
    if (!window.confirm('¿Querés darte de baja del Destacado? No se te cobra más y seguís destacado hasta la fecha que ya pagaste.')) return;
    accion(async () => { await api.comercioDestacadoBaja(); success('Listo, te diste de baja del Destacado. No se te va a cobrar más.'); onCambio(); });
  };
  const aprobado = comercio.estado === 'aprobado';
  return (
    <div className={`comercio-card ${estado.activo ? 'comercio-destacado--activo' : ''}`}>
      <h3 style={{ fontFamily: 'var(--font-display)', color: 'white', marginBottom: 6 }}>⭐ Destacá tu local</h3>
      <p className="regalo-note" style={{ marginBottom: 12 }}>
        Publicar es gratis. Con Destacado tu local sale primero en Cupido Descuentos, como «📍 lugar recomendado para la primera cita» en el chat y sugerido en la Cita segura.
        Cuesta {fmtARS(estado.precio)} por mes con Mercado Pago (se cobra solo cada mes y te podés dar de baja cuando quieras).
      </p>
      {estado.activo && (
        <div className="comercio-validado" style={{ marginTop: 0, marginBottom: 12 }}>
          ✅ Tu local está destacado hasta el {fecha(estado.hasta)}.{estado.suscripcion === 'activa' ? ' Tu suscripción se renueva sola cada mes.' : ''}
        </div>
      )}
      {estado.suscripcion === 'pendiente' && (
        <p className="regalo-note" style={{ marginBottom: 12 }}>⏳ Falta que autorices la suscripción en Mercado Pago. Si ya lo hiciste, se activa en unos minutos.</p>
      )}
      {!aprobado ? <p className="regalo-note">Vas a poder destacarte cuando te aprobemos.</p> : (
        <div className="regalo-actions" style={{ marginTop: 0 }}>
          {estado.prueba_disponible && estado.suscripcion !== 'activa' && <button className="btn btn-primary btn-sm" disabled={busy} onClick={prueba}>🎁 Probar 1 mes gratis</button>}
          {estado.suscripcion === 'activa'
            ? <button className="btn btn-outline btn-sm" disabled={busy} onClick={baja}>Darme de baja del Destacado</button>
            : <button className={`btn btn-sm ${estado.prueba_disponible ? 'btn-outline' : 'btn-primary'}`} disabled={busy} onClick={suscribir}>
                💳 Suscribirme · {fmtARS(estado.precio)} por mes
              </button>}
          {estado.suscripcion === 'pendiente' && <button className="btn btn-ghost btn-sm" disabled={busy} onClick={baja}>Cancelar</button>}
        </div>
      )}
    </div>
  );
}

function Descuentos({ comercio, success, error }) {
  const [d, setD] = useState(null);
  const [editando, setEditando] = useState(null);
  const [nuevoKey, setNuevoKey] = useState(0);
  const load = useCallback(() => api.comercioDescuentos().then(setD).catch(e => error(e.message)), [error]);
  useEffect(() => { load(); }, [load]);

  const guardar = (id) => async (data) => {
    try {
      if (id) await api.comercioEditarDescuento(id, data); else await api.comercioCrearDescuento(data);
      success(id ? 'Descuento actualizado.' : 'Descuento publicado.');
      setEditando(null);
      if (!id) setNuevoKey(k => k + 1);
      load();
    } catch (err) { error(err.message); }
  };
  const toggle = async (x) => {
    try { await api.comercioEditarDescuento(x.id, { activo: !x.activo }); load(); } catch (err) { error(err.message); }
  };

  if (!d) return <p className="regalos-empty">Cargando...</p>;
  return (
    <div>
      <ValidarCodigo onValidado={load} />
      <Destacado comercio={comercio} estado={d.destacado} onCambio={load} success={success} error={error} />
      <div className="comercio-totales">
        <div><span>Clientes este mes</span><strong>{d.totales.canjeados_mes}</strong></div>
        <div><span>Clientes en total</span><strong>{d.totales.canjeados}</strong></div>
        <div><span>Códigos pedidos</span><strong>{d.totales.codigos}</strong></div>
      </div>
      <p className="regalo-note" style={{ marginBottom: 12 }}>
        Tus descuentos aparecen en Cupido Descuentos{comercio.estado !== 'aprobado' ? ' cuando te aprobemos' : ''} y como lugar sugerido cuando alguien arma su Cita segura.
      </p>
      {editando === null && d.descuentos.length < d.max && (
        <DescuentoForm key={`nuevo-${nuevoKey}`} categorias={d.categorias} onGuardar={guardar(null)} />
      )}
      <h3 className="regalos-section-title">Mis descuentos</h3>
      {d.descuentos.length === 0 ? <p className="regalos-empty">Todavía no publicaste descuentos.</p>
        : d.descuentos.map(x => editando === x.id ? (
          <DescuentoForm key={x.id} inicial={x} categorias={d.categorias} onGuardar={guardar(x.id)} onCancelar={() => setEditando(null)} />
        ) : (
          <div key={x.id} className="comercio-producto" style={{ marginBottom: 8, ...(x.activo && !x.pausado_admin ? {} : { opacity: 0.6 }) }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div className="regalo-name" style={{ fontSize: 14 }}>
                {x.descuento} · <span style={{ fontWeight: 400 }}>{x.categoria}</span>{' '}
                {!x.activo && <span className="badge badge-gris">Pausado</span>}
                {x.pausado_admin ? <span className="badge badge-gris">Pausado por Cupido con Mate</span> : null}
                {x.patrocinado ? <span className="badge badge-dorado">📍 Destacado</span> : null}
              </div>
              <div className="regalo-detail" style={{ marginTop: 0 }}>
                {x.direccion || 'Sin dirección'}{x.condiciones ? ` · ${x.condiciones}` : ''} · 🎟️ {x.codigos} pedidos · ✅ {x.canjeados} usados
              </div>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setEditando(x.id)}>✏️ Editar</button>
            <button className="btn btn-outline btn-sm" onClick={() => toggle(x)}>{x.activo ? 'Pausar' : 'Activar'}</button>
          </div>
        ))}
      {d.recientes.length > 0 && <>
        <h3 className="regalos-section-title">Últimos códigos usados</h3>
        <div className="comercio-card">
          {d.recientes.map(r => <div key={r.codigo} className="regalo-detail" style={{ marginTop: 2 }}>{fecha(r.canjeado_at)} · {r.codigo} · {r.descuento}</div>)}
        </div>
      </>}
    </div>
  );
}

// El mapa (Leaflet) se carga solo cuando hace falta
const Mapa = lazy(() => import('../components/Mapa'));

// Horario de atención: fuera de horario el comercio no aparece en Regalos y +
const DIAS = [['1', 'Lunes'], ['2', 'Martes'], ['3', 'Miércoles'], ['4', 'Jueves'], ['5', 'Viernes'], ['6', 'Sábado'], ['0', 'Domingo']];

function HorarioAtencion({ comercio, onCambio, success, error }) {
  const [h, setH] = useState(() => {
    const base = comercio.horarios || {};
    return Object.fromEntries(DIAS.map(([d]) => [d, (base[d] || []).map(t => [...t])]));
  });
  const [busy, setBusy] = useState(false);
  const setTurno = (d, i, j, v) => setH({ ...h, [d]: h[d].map((t, k) => k === i ? (j === 0 ? [v, t[1]] : [t[0], v]) : t) });
  const abrir = (d, si) => setH({ ...h, [d]: si ? [['09:00', '13:00']] : [] });
  const agregarTurno = (d) => setH({ ...h, [d]: [...h[d], ['17:00', '21:00']] });
  const sacarTurno = (d, i) => setH({ ...h, [d]: h[d].filter((_, k) => k !== i) });
  const copiarLunes = () => setH(Object.fromEntries(DIAS.map(([d]) => [d, h['1'].map(t => [...t])])));
  const guardar = async () => {
    setBusy(true);
    try { onCambio((await api.comercioHorarios(h)).comercio); success('Horario guardado.'); }
    catch (err) { error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="comercio-card">
      <h3 style={{ fontFamily: 'var(--font-display)', color: 'white', marginBottom: 6 }}>🕒 Horario de atención</h3>
      <p className="regalo-note" style={{ marginBottom: 12 }}>
        Tus productos aparecen en Regalos y + solo mientras estás abierto (hora de San Juan). Podés cargar hasta 2 turnos por día; si cerrás después de medianoche, poné la hora igual (por ejemplo 20:00 a 02:00).
      </p>
      <div className="horario-dias">
        {DIAS.map(([d, nombre]) => (
          <div key={d} className="horario-dia">
            <label className="comercio-check" style={{ margin: 0, minWidth: 120 }}>
              <input type="checkbox" checked={h[d].length > 0} onChange={e => abrir(d, e.target.checked)} />
              <span style={{ color: 'white', fontWeight: 600 }}>{nombre}</span>
            </label>
            {h[d].length === 0 ? <span className="regalo-note">Cerrado</span> : (
              <div className="horario-turnos">
                {h[d].map((t, i) => (
                  <div key={i} className="horario-turno">
                    <input className="form-input" type="time" value={t[0]} onChange={e => setTurno(d, i, 0, e.target.value)} aria-label={`${nombre} desde`} />
                    <span className="regalo-note">a</span>
                    <input className="form-input" type="time" value={t[1]} onChange={e => setTurno(d, i, 1, e.target.value)} aria-label={`${nombre} hasta`} />
                    {i > 0 && <button type="button" className="btn btn-outline btn-sm" onClick={() => sacarTurno(d, i)} aria-label="Quitar turno">✕</button>}
                  </div>
                ))}
                {h[d].length < 2 && <button type="button" className="horario-mas" onClick={() => agregarTurno(d)}>+ turno tarde</button>}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="regalo-actions">
        {h['1'].length > 0 && <button type="button" className="btn btn-outline btn-sm" onClick={copiarLunes}>Copiar el lunes a todos los días</button>}
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={guardar}>{busy ? 'Guardando...' : 'Guardar horario'}</button>
      </div>
    </div>
  );
}

// Zona de entrega: el local en el mapa y un radio de hasta 3 km
function ZonaEntrega({ comercio, onCambio, success, error }) {
  const [punto, setPunto] = useState(comercio.zona ? { lat: comercio.zona.lat, lng: comercio.zona.lng } : null);
  const [radio, setRadio] = useState(comercio.zona?.radio_km || comercio.radio_max_km);
  const [busy, setBusy] = useState(false);
  const elegir = useCallback((lat, lng) => setPunto({ lat, lng }), []);
  const circulo = useMemo(() => punto && { ...punto, radio_km: radio }, [punto, radio]);

  const miUbicacion = () => {
    if (!navigator.geolocation) { error('Tu navegador no comparte la ubicación. Tocá el mapa donde está tu local.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setPunto({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => error('No pudimos obtener tu ubicación. Tocá el mapa donde está tu local.'),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  const guardar = async () => {
    if (!punto) { error('Tocá el mapa donde está tu local.'); return; }
    setBusy(true);
    try { onCambio((await api.comercioZona({ ...punto, radio_km: radio })).comercio); success('Zona de entrega guardada.'); }
    catch (err) { error(err.message); }
    finally { setBusy(false); }
  };

  if (!comercio.mapas) return null;
  return (
    <div className="comercio-card">
      <h3 style={{ fontFamily: 'var(--font-display)', color: 'white', marginBottom: 6 }}>📍 Zona de entrega</h3>
      <p className="regalo-note" style={{ marginBottom: 12 }}>
        Tocá el mapa donde está tu local (o arrastrá el pin) y elegí hasta cuántos km entregás, como máximo {comercio.radio_max_km} km.
        Solo pueden aceptar tus regalos las personas que los reciben dentro del círculo.
      </p>
      <Suspense fallback={<p className="regalo-note">Cargando mapa…</p>}><Mapa circulo={circulo} marcador={punto} onElegir={elegir} /></Suspense>
      <div className="comercio-zona-controles">
        <label className="form-label" htmlFor="radio-entrega">Entrego hasta <strong style={{ color: 'white' }}>{radio} km</strong></label>
        <input id="radio-entrega" type="range" min="0.5" max={comercio.radio_max_km} step="0.5" value={radio}
          onChange={e => setRadio(parseFloat(e.target.value))} />
        <div className="regalo-actions" style={{ marginTop: 0 }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={miUbicacion}>📍 Usar mi ubicación</button>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || !punto} onClick={guardar}>{busy ? 'Guardando...' : 'Guardar zona'}</button>
        </div>
      </div>
    </div>
  );
}

function MiComercio({ comercio, onCambio, success, error }) {
  const [f, setF] = useState({ descripcion: comercio.descripcion || '', telefono: comercio.telefono || '' });
  const seccion = async (k, valor) => {
    try { onCambio((await api.comercioEditar({ [k]: valor })).comercio); success('Listo.'); } catch (err) { error(err.message); }
  };
  const guardar = async (e) => {
    e.preventDefault();
    try { onCambio((await api.comercioEditar(f)).comercio); success('Datos guardados.'); } catch (err) { error(err.message); }
  };
  const subirLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('photo', file);
    try { const { logo_path } = await api.comercioLogo(fd); onCambio({ ...comercio, logo_path }); success('Logo actualizado.'); }
    catch (err) { error(err.message); }
  };
  return (
    <form className="comercio-card" onSubmit={guardar}>
      <div className="comercio-logo-editar">
        {comercio.logo_path
          ? <img src={comercio.logo_path} alt="Logo" className="comercio-logo comercio-logo--grande" />
          : <span className="comercio-logo comercio-logo--grande comercio-logo--vacio">🏪</span>}
        <div>
          <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
            📷 {comercio.logo_path ? 'Cambiar logo' : 'Subir logo'}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { subirLogo(e.target.files[0]); e.target.value = ''; }} />
          </label>
          <p className="regalo-note" style={{ marginTop: 6 }}>Aparece al lado del nombre de tu comercio en Regalos y + y en Cupido Descuentos. Mejor cuadrado, JPG o PNG.</p>
        </div>
      </div>
      <div className="regalo-detail" style={{ marginBottom: 12 }}>
        <strong>{comercio.nombre}</strong>{comercio.en_delivery ? ` · ${comercio.categoria}` : ''} · {comercio.email}
      </div>
      <div className="comercio-secciones">
        <label className="comercio-check">
          <input type="checkbox" checked={comercio.en_delivery} onChange={e => seccion('en_delivery', e.target.checked)} />
          <span><strong>🛵 Cupido Delivery:</strong> vendo regalos y los envío.</span>
        </label>
        <label className="comercio-check">
          <input type="checkbox" checked={comercio.en_descuentos} onChange={e => seccion('en_descuentos', e.target.checked)} />
          <span><strong>🏷️ Cupido Descuentos:</strong> publico descuentos para citas en mi local.</span>
        </label>
      </div>
      <div className="comercio-form-grid">
        <div className="form-group"><label className="form-label">Descripción</label>
          <input className="form-input" value={f.descripcion} maxLength={300} onChange={e => setF({ ...f, descripcion: e.target.value })} /></div>
        <div className="form-group"><label className="form-label">Teléfono</label>
          <input className="form-input" value={f.telefono} maxLength={40} onChange={e => setF({ ...f, telefono: e.target.value })} /></div>
      </div>
      <p className="regalo-note" style={{ marginBottom: 12 }}>Para cambiar el nombre, la categoría o el correo, escribile al equipo de Cupido con Mate.</p>
      <button type="submit" className="btn btn-primary btn-sm">Guardar</button>
    </form>
  );
}

// ── Página ────────────────────────────────────────────────────
// marca: 'delivery' en /comercio (Cupido Delivery), 'descuentos' en /descuentos (Cupido Descuentos).
// Es la misma cuenta de comercio; cambian la marca, el registro, la ayuda y el asistente.
export default function ComercioPage({ marca = 'delivery' }) {
  const { toasts, success, error } = useToast();
  const [comercio, setComercio] = useState(undefined);
  const [tab, setTab] = useState(null);
  const [ayuda, setAyuda] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const desc = marca === 'descuentos';
  const conToken = new URLSearchParams(location.search).has('token');

  useEffect(() => {
    // Enlaces viejos /comercio?descuentos=1 → panel de Cupido Descuentos
    if (!desc && new URLSearchParams(location.search).get('descuentos') === '1') { navigate('/descuentos', { replace: true }); return; }
    if (location.pathname === '/comercio/activar' || conToken) { setComercio(null); return; }
    api.comercioYo().then(d => setComercio(d.comercio)).catch(() => setComercio(null));
  }, [location.pathname]);

  // Vuelta de Mercado Pago después de pagar el Destacado
  useEffect(() => {
    const pago = new URLSearchParams(location.search).get('pago');
    if (!pago) return;
    if (pago === 'ok') success('💳 ¡Listo! Tu suscripción al Destacado se activa en unos minutos.');
    else error('La suscripción no se completó. Podés intentarlo de nuevo.');
    setTab('descuentos');
    navigate(location.pathname, { replace: true });
  }, []);

  // Si se llega navegando dentro del sitio, pasar a la app "Cupido Delivery" o "Cupido Descuentos"
  // (al entrar directo a /comercio o /descuentos ya lo hace el servidor)
  useEffect(() => {
    const manifest = document.querySelector('link[rel="manifest"]');
    const icono = document.querySelector('link[rel="apple-touch-icon"]');
    manifest?.setAttribute('href', desc ? '/manifest-descuentos.json' : '/manifest-comercio.json');
    icono?.setAttribute('href', desc ? '/descuentos-apple-touch.png' : '/delivery-apple-touch.png');
    document.title = desc ? 'Cupido Descuentos' : 'Cupido Delivery';
    // Al salir del panel vuelve a ser Cupido con Mate (mismos valores que public/index.html)
    return () => {
      manifest?.setAttribute('href', '/manifest.json');
      icono?.setAttribute('href', '/apple-touch-icon.png?v=4');
      document.title = 'Cupido con Mate San Juan 🧉';
    };
  }, [desc]);

  // Pestañas según dónde está el comercio: Delivery (pedidos, productos), Descuentos o los dos
  const tabs = comercio ? [
    ...(comercio.en_delivery ? [['pedidos', '🛵 Pedidos'], ['productos', '🎁 Productos']] : []),
    ...(comercio.en_descuentos ? [['descuentos', '🏷️ Descuentos']] : []),
    ['comercio', '🏪 Mi comercio'],
  ] : [];
  // En /descuentos arranca en la pestaña Descuentos aunque el comercio también esté en Delivery
  const tabInicial = desc && comercio?.en_descuentos ? 'descuentos' : tabs[0]?.[0];
  const tabActual = tabs.some(([id]) => id === tab) ? tab : tabInicial;
  const delivery = Boolean(comercio?.en_delivery);

  const salir = async () => { await quitarAvisos('comercio'); try { await api.comercioSalir(); } catch { /* igual se cierra */ } setComercio(null); };
  const aceptarTerminos = async () => {
    try { await api.comercioTerminos(); setComercio({ ...comercio, terminos_aceptados: true }); success('Condiciones aceptadas.'); }
    catch (err) { error(err.message); }
  };

  return (
    <div className="regalos-page">
      <ToastContainer toasts={toasts} />
      <div className="regalos-container container">
        <div className="regalos-header comercio-header">
          {desc || (comercio && !comercio.en_delivery)
            ? <img src={DESCUENTOS} srcSet={DESCUENTOS_SRCSET} sizes="(max-width: 600px) 90px, 120px" alt="Cupido Descuentos" className="cupido-delivery-logo" />
            : <img src={DELIVERY} srcSet={DELIVERY_SRCSET} sizes="(max-width: 600px) 90px, 120px" alt="Cupido Delivery" className="cupido-delivery-logo" />}
          <div className="comercio-header-titulo">
            {comercio
              ? <ComercioNombre comercio={comercio} className="regalos-title" />
              : <h1 className="regalos-title">{desc ? '🏷️ Panel de locales' : '🏪 Panel de comercios'}</h1>}
            <p className="regalos-sub">{comercio?.en_delivery && comercio?.en_descuentos ? 'Cupido Delivery y Descuentos' : desc || (comercio && !comercio.en_delivery) ? 'Cupido Descuentos' : 'Cupido Delivery'} · Cupido con Mate San Juan</p>
          </div>
          {comercio && (
            <div className="admin-actions">
              <button className="btn btn-outline btn-sm" onClick={() => setAyuda(true)}>❓ Ayuda</button>
              <button className="btn btn-outline btn-sm" onClick={salir}>Salir</button>
            </div>
          )}
        </div>

        {comercio === undefined ? <p className="regalos-empty">Cargando...</p>
          : comercio === null ? <Acceso marca={marca} onListo={setComercio} success={success} error={error} />
          : comercio.falta_nacimiento ? <Nacimiento onCambio={setComercio} error={error} />
          : (
            <>
              <ActivarAvisos key={comercio.id} tipo="comercio" />
              {comercio.estado === 'pendiente' && (
                <div className="comercio-aviso">⏳ <strong>Tu solicitud está en revisión.</strong> {delivery
                  ? 'Ya podés cargar tus productos; van a aparecer en Cupido Delivery cuando te aprobemos.'
                  : 'Ya podés cargar tus descuentos; van a aparecer en Cupido Descuentos cuando te aprobemos.'} Te avisamos por correo.</div>
              )}
              {delivery && comercio.estado === 'aprobado' && !comercio.conectado && (
                <div className="comercio-aviso">
                  💳 <strong>Conectá tu Mercado Pago para empezar a vender.</strong> Los pagos te llegan directo a tu cuenta.
                  {comercio.connect_url
                    ? <div style={{ marginTop: 10 }}><a className="btn btn-primary btn-sm" href={comercio.connect_url}>Conectar Mercado Pago</a></div>
                    : <div className="regalo-note" style={{ marginTop: 6 }}>La conexión todavía no está habilitada. Escribile al equipo de Cupido con Mate.</div>}
                </div>
              )}
              {delivery && !comercio.horarios && (
                <div className="comercio-aviso">
                  🕒 <strong>Cargá tu horario de atención.</strong> Tus productos aparecen en Regalos y + solo mientras estás abierto.
                  {tabActual !== 'comercio' && <div style={{ marginTop: 10 }}><button className="btn btn-primary btn-sm" onClick={() => setTab('comercio')}>Cargar horario</button></div>}
                </div>
              )}
              {delivery && comercio.horarios && comercio.estado === 'aprobado' && (
                <div className="regalo-note" style={{ marginBottom: 12 }}>
                  {comercio.abierto ? '🟢 Abierto ahora: tus productos se ven en Regalos y +.' : '🔴 Cerrado ahora: tus productos no se ven en Regalos y + hasta que abras.'}
                </div>
              )}
              {delivery && comercio.mapas && !comercio.zona && (
                <div className="comercio-aviso">
                  📍 <strong>Marcá tu zona de entrega en el mapa</strong> (hasta {comercio.radio_max_km} km de tu local). Sin zona, tus productos no aparecen en Regalos y +.
                  {tabActual !== 'comercio' && <div style={{ marginTop: 10 }}><button className="btn btn-primary btn-sm" onClick={() => setTab('comercio')}>Marcar mi zona</button></div>}
                </div>
              )}
              {delivery && !comercio.terminos_aceptados && (
                <div className="comercio-aviso">
                  <Leyenda />
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={aceptarTerminos}>Acepto las condiciones de envío</button>
                </div>
              )}

              <div className="admin-tabs" style={{ flexWrap: 'wrap' }}>
                {tabs.map(([id, txt]) => (
                  <button key={id} className={`admin-tab ${tabActual === id ? 'active' : ''}`} onClick={() => setTab(id)}>{txt}</button>
                ))}
              </div>
              {tabActual === 'pedidos' && <Pedidos comercio={comercio} error={error} />}
              {tabActual === 'descuentos' && <Descuentos comercio={comercio} success={success} error={error} />}
              {tabActual === 'productos' && (comercio.terminos_aceptados
                ? <Productos comercio={comercio} success={success} error={error} />
                : <p className="regalos-empty">Aceptá las condiciones de envío para cargar productos.</p>)}
              {tabActual === 'comercio' && <>
                {delivery && <HorarioAtencion comercio={comercio} onCambio={setComercio} success={success} error={error} />}
                {delivery && <ZonaEntrega comercio={comercio} onCambio={setComercio} success={success} error={error} />}
                <MiComercio comercio={comercio} onCambio={setComercio} success={success} error={error} />
              </>}
              {delivery && <div style={{ marginTop: 24 }}><Leyenda /></div>}
            </>
          )}
      </div>
      {ayuda && <AyudaModal marca={desc || (comercio && !comercio.en_delivery) ? 'descuentos' : 'delivery'} onClose={() => setAyuda(false)} />}
    </div>
  );
}
